import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ShoppingCart, Download } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SiteSelect } from "@/components/SiteSelect";

const MONTH_LABELS = { "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun", "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic" };
const MONTHS_ORDER = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const BALANCE_COLORS = ["#0d9488", "#BF3437", "#2563eb", "#d97706", "#7c3aed", "#16a34a", "#0891b2", "#db2777", "#65a30d", "#78716c"];
const thisYear = new Date().getFullYear();
const YEARS = [thisYear - 2, thisYear - 1, thisYear, thisYear + 1, thisYear + 2];
const money = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
const pct = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function OpsTasksAnalytics() {
  const { user } = useAuth();
  const backLink = user?.role === "admin" ? "/admin" : "/supervisor";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [year, setYear] = useState(String(thisYear));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hiddenSites, setHiddenSites] = useState(new Set());

  useEffect(() => { setHiddenSites(new Set()); }, [clientId]);

  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get("/clients"); setClients((d || []).filter((c) => c.ops_tasks_enabled)); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/ops-tasks/analytics", { params: { year, month, client_id: clientId || undefined, site_id: siteId || undefined } });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year, month, clientId, siteId]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/ops-tasks/export.csv", { params: { client_id: clientId || undefined, site_id: siteId || undefined, year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "tareas_operativas.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/ops-tasks/export.pdf", { params: { client_id: clientId || undefined, site_id: siteId || undefined, year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "tareas_operativas.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSite = (name) => setHiddenSites((prev) => {
    const n = new Set(prev);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });

  const recuperosChart = data ? data.recuperos_monthly.map((m) => ({ month: MONTH_LABELS[m.month], monto: m.amount })) : [];
  const compareChart = data ? data.decomisos_monthly.map((m, i) => ({ month: MONTH_LABELS[m.month], decomisos: m.quantity, devoluciones: data.devoluciones_monthly[i]?.quantity || 0 })) : [];
  const faltantesChart = data ? data.faltantes_monthly.map((m) => ({ month: MONTH_LABELS[m.month], faltantes: m.quantity })) : [];
  const ticketsChart = data ? data.tickets_monthly.map((m) => ({ month: MONTH_LABELS[m.month], tickets: m.count })) : [];
  const balanceSites = data ? data.balance_by_site : [];
  const balanceSiteChartData = MONTHS_ORDER.map((m) => {
    const row = { month: MONTH_LABELS[m] };
    balanceSites.forEach((s) => { row[s.site_name] = s.months[m]; });
    return row;
  });

  return (
    <div style={{ padding: "20px 24px 60px" }} data-testid="ops-tasks-analytics-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 14, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14} /> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "0 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}><ShoppingCart size={22} style={{ display: "inline", marginRight: 8, color: "#BF3437" }} />Tareas Operativas · Analíticas</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 16 }}>Recuperos, decomisos, devoluciones, faltantes de perfiles y control de tickets, por objetivo/sede/año.</p>

      {clients.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: "center" }} data-testid="ops-analytics-no-clients">
          <div style={{ fontSize: 13, fontWeight: 800 }}>Ningún objetivo tiene el módulo de Tareas Operativas habilitado</div>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>Activalo desde la ficha del cliente (pestaña Información).</div>
        </div>
      ) : (
        <>
          <div className="card" style={{ padding: 12, marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }} data-testid="ops-analytics-filters">
            <div className="field" style={{ margin: 0, minWidth: 200 }}>
              <label>Objetivo</label>
              <select value={clientId} onChange={(e) => { setClientId(e.target.value); setSiteId(""); }} data-testid="ops-analytics-filter-client">
                <option value="">Todos los objetivos</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {clientId && <SiteSelect clientId={clientId} value={siteId} onChange={setSiteId} testIdPrefix="ops-analytics" />}
            <div className="field" style={{ margin: 0, minWidth: 120 }}>
              <label>Año</label>
              <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="ops-analytics-filter-year">
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="field" style={{ margin: 0, minWidth: 150 }}>
              <label>Mes (pendientes de Balance)</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="ops-analytics-filter-month">
                {MONTHS_ORDER.map((m) => <option key={m} value={m}>{MONTH_LABELS[m]}</option>)}
              </select>
            </div>
            <button type="button" onClick={exportCsv} className="btn-ghost" style={{ padding: "10px 14px", fontSize: 12 }} data-testid="ops-analytics-export-csv">
              <Download size={13} style={{ marginRight: 6 }} />Exportar detalle CSV
            </button>
            <button type="button" onClick={exportPdf} className="btn-ghost" style={{ padding: "10px 14px", fontSize: 12 }} data-testid="ops-analytics-export-pdf">
              <Download size={13} style={{ marginRight: 6 }} />Exportar PDF
            </button>
          </div>

          {loading && <div className="skel" style={{ height: 220 }} />}

          {!loading && data && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 18 }}>
                <div className="card" style={{ padding: 14 }} data-testid="ops-total-recuperos"><div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Recuperos ({year})</div><div style={{ fontSize: 20, fontWeight: 900, color: "#16a34a" }}>{money(data.totals.recuperos_amount)}</div></div>
                <div className="card" style={{ padding: 14 }} data-testid="ops-total-decomisos"><div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Decomisos</div><div style={{ fontSize: 20, fontWeight: 900, color: "#BF3437" }}>{data.totals.decomisos_qty} u.</div></div>
                <div className="card" style={{ padding: 14 }} data-testid="ops-total-devoluciones"><div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Devoluciones</div><div style={{ fontSize: 20, fontWeight: 900, color: "#2563eb" }}>{data.totals.devoluciones_qty} u.</div></div>
                <div className="card" style={{ padding: 14 }} data-testid="ops-total-faltantes"><div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Faltantes en perfiles</div><div style={{ fontSize: 20, fontWeight: 900, color: "#7c3aed" }}>{data.totals.faltantes_qty} u.</div></div>
                <div className="card" style={{ padding: 14 }} data-testid="ops-total-tickets"><div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Tickets controlados</div><div style={{ fontSize: 20, fontWeight: 900, color: "#0891b2" }}>{data.totals.tickets_count}</div></div>
                {data.totals.balance_avg !== null && (
                  <div className="card" style={{ padding: 14 }} data-testid="ops-total-balance"><div style={{ fontSize: 10, color: "var(--invict-text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Balance promedio</div><div style={{ fontSize: 20, fontWeight: 900, color: "#0d9488" }}>{pct(data.totals.balance_avg)}%</div></div>
                )}
              </div>

              <div className="card" style={{ padding: 16, marginBottom: 16 }} data-testid="ops-chart-recuperos">
                <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Recuperos ($) por mes</h4>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={recuperosChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fontWeight: 700 }} />
                      <YAxis tick={{ fontSize: 11 }} width={60} />
                      <Tooltip formatter={(v) => [money(v), "Monto"]} />
                      <Bar dataKey="monto" fill="#16a34a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card" style={{ padding: 16, marginBottom: 16 }} data-testid="ops-chart-compare">
                <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Decomisos vs. Devoluciones (cantidad) por mes</h4>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={compareChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fontWeight: 700 }} />
                      <YAxis tick={{ fontSize: 11 }} width={40} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="decomisos" fill="#BF3437" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="devoluciones" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div className="card" style={{ padding: 16 }} data-testid="ops-chart-faltantes">
                  <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Faltantes en perfiles por mes</h4>
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={faltantesChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fontWeight: 700 }} />
                        <YAxis tick={{ fontSize: 10 }} width={30} />
                        <Tooltip />
                        <Bar dataKey="faltantes" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="card" style={{ padding: 16 }} data-testid="ops-chart-tickets">
                  <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Control de tickets por mes</h4>
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ticketsChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fontWeight: 700 }} />
                        <YAxis tick={{ fontSize: 10 }} width={30} />
                        <Tooltip />
                        <Bar dataKey="tickets" fill="#0891b2" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {balanceSites.length > 0 && (
                <div className="card" style={{ padding: 16, marginBottom: 16 }} data-testid="ops-chart-balance">
                  <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Balance (%) por sucursal, por mes</h4>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    {balanceSites.map((s, i) => {
                      const color = BALANCE_COLORS[i % BALANCE_COLORS.length];
                      const hidden = hiddenSites.has(s.site_name);
                      return (
                        <button key={s.site_name} type="button" onClick={() => toggleSite(s.site_name)}
                          data-testid={`ops-balance-legend-${s.site_name}`}
                          style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, border: `1px solid ${color}`, background: hidden ? "transparent" : `${color}18`, fontSize: 11, fontWeight: 700, opacity: hidden ? 0.4 : 1, cursor: "pointer" }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                          {s.site_name}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={balanceSiteChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fontWeight: 700 }} />
                        <YAxis tick={{ fontSize: 11 }} width={45} />
                        <Tooltip formatter={(v) => [`${pct(v)}%`, ""]} />
                        {balanceSites.map((s, i) => !hiddenSites.has(s.site_name) && (
                          <Bar key={s.site_name} dataKey={s.site_name} fill={BALANCE_COLORS[i % BALANCE_COLORS.length]} radius={[4, 4, 0, 0]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {data.recuperos_by_site.length > 0 && (
                <div className="card" style={{ padding: 16 }} data-testid="ops-recuperos-by-site">
                  <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Recuperos por sede</h4>
                  {data.recuperos_by_site.map((s) => (
                    <div key={s.site_name} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`ops-site-row-${s.site_name}`}>
                      <span>{s.site_name}</span><b>{money(s.total)}</b>
                    </div>
                  ))}
                </div>
              )}

              {data.balance_total_sites > 0 && (
                <div className="card" style={{ padding: 16, marginTop: 16 }} data-testid="ops-balance-by-site">
                  <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800 }}>Balance por sucursal</h4>
                  <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 10, color: data.balance_registered_count < data.balance_total_sites ? "#BF3437" : "#16a34a" }} data-testid="ops-balance-registered-counter">
                    {data.balance_registered_count} de {data.balance_total_sites} sucursales registraron el balance de {data.balance_period_label}
                  </div>
                  {data.balance_pending_sites.map((s) => (
                    <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`ops-balance-site-row-${s.name}`}>
                      <span>{s.name}</span>
                      <b style={{ color: "#BF3437", background: "#BF343718", padding: "2px 10px", borderRadius: 10, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>Pendiente</b>
                    </div>
                  ))}
                  {data.balance_registered_sites.map((s) => {
                    const siteData = balanceSites.find((b) => b.site_name === s.name);
                    return (
                      <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`ops-balance-site-row-${s.name}`}>
                        <span>{s.name}</span>
                        <b style={{ color: "#0d9488" }}>{siteData ? `${pct(siteData.avg)}%` : "—"}</b>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
